import { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { Box, CircularProgress } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import TaskAnalysisEChart from "./TaskAnalysisECharts";
import Task from "../model/Task";
import { GetTaskAnalysis } from "../operations/TaskOperations";
import { GetPackageAnalysis } from "../operations/TaskPackageOperations";
import { currencyFormat, percentFormat} from '../util/Formatters'
import { useViewState } from '../model/ViewState'
import { makeAutoObservable } from "mobx";


const TaskAnalysisTable = observer(({ selectedTask }) => {
  if (!selectedTask.analysis) {
    selectedTask.analysis = [];
  }
  const viewState = useViewState();

  useEffect(() =>{
    if (viewState.analysisTabViewMode === "table") {
      viewState.analysisTableLoading = true;
      if (selectedTask.isPackage){
        GetPackageAnalysis(selectedTask.id, (result) => {
          selectedTask.analysis = result;
          viewState.analysisTableLoading = false;
        });
      } else {
        GetTaskAnalysis(selectedTask.id, (result) => {
          selectedTask.analysis = result;
          viewState.analysisTableLoading = false;
        });
      }
      
    }
  }, [selectedTask, viewState.analysisTabViewMode]);

  const columns = [
    {
      field: "id",
      headerName: "Period",
      width: 80,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "progress",
      headerName: "Progress",
      width: 80,
      align: "right",
      headerAlign: "right",
      cellClassName: "uneditableExp",
      valueFormatter: percentFormat
    },
    {
      field: "budgetSpent",
      headerName: <span>Budget<br />Spent</span>,
      width: 100,
      align: "right",
      headerAlign: "right",
      valueFormatter: percentFormat,
    },
    {
      field: "plannedProgress",
      headerName: <span>Planned<br />Progress</span>,
      width: 100,
      align: "right",
      headerAlign: "right",
      valueFormatter: percentFormat
    },
    {
      field: "BAC",
      headerName: "BAC",
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: currencyFormat
    },
    {
      field: "AC",
      headerName: "AC",
      width: 140,
      align: "right",
      headerAlign: "right",
      cellClassName: "uneditableExp",
      valueFormatter: currencyFormat
    },
    {
      field: "PV",
      headerName: "PV",
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: currencyFormat
    },
    {
      field: "ES",
      headerName: "ES",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "EV",
      headerName: "EV",
      editable: false,
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: currencyFormat
    },
    {
      field: "CV",
      headerName: "CV",
      editable: false,
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: currencyFormat
    },
    {
      field: "SVDollahs",
      headerName: "SV($)",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "SVtime",
      headerName: "SV(t)",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "CPI",
      headerName: "CPI",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "CPIPeriod",
      headerName: "CPIp",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "SPIDollahs",
      headerName: "SPI($)",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "SPIDollahsPeriod",
      headerName: "SPI($)p",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "SPITime",
      headerName: "SPI(t)",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "SPITimePeriod",
      headerName: "SPI(t)p",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "SPITimeWeighted",
      headerName: "SPI(t) (avg)",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
    {
      field: "SPITimeWorking",
      headerName: "SPI(t) (working agg)",
      editable: false,
      width: 80,
      align: "right",
      headerAlign: "right",
    },
  ];
  const rows = selectedTask.analysis.map((row) => ({
    id: row.period,
    progress: row.actualProgress * 100,
    budgetSpent:row.budgetSpent * 100,
    plannedProgress:row.plannedProgress * 100,
    BAC: row.budgetAtCompletion,
    AC:row.actualCost,
    PV:row.plannedValue,
    ES:row.earnedSchedule,
    EV:row.earnedValue,
    CV:row.costVariance,
    SVDollahs:row.scheduleVarianceCost,
    SVtime:row.scheduleVarianceTime,
    CPI:row.costPerformanceIndex,
    CPIPeriod:row.costPerformanceIndexPeriod,
    SPIDollahs:row.schedulePerformanceIndexCost,
    SPIDollahsPeriod:row.schedulePerformanceIndexCostPeriod,
    SPITime: row.schedulePerformanceIndexTime,
    SPITimePeriod:row.schedulePerformanceIndexTimePeriod,
    SPITimeWeighted:row.weightedAverageSchedulePerformanceIndexTime,
    SPITimeWorking: row.workingSchedulePerformanceIndexTime,
    PreBase: row.isPreBase,
  }));

  return (
    <div>
      {false ? (
        <Box
          sx={{
            height: 900,
            width: 1425,
            "& .uneditableExp": {
              backgroundColor: "rgba(225, 255, 255, 0)",
              color: "#808080",
              fontStyle: "italic",
            },
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Box
          sx={{
            height: 550,
            width: 1250,
            "& .editable": {
              backgroundColor: "rgba(225, 255, 255, 0)",
              color: "#000000",
            },
            "& .uneditable": {
              backgroundColor: "rgba(225, 255, 255, 0)",
              color: "#000000",
            },
            "& .uneditableExp": {
              backgroundColor: "rgba(225, 255, 255, 0)",
              color: "#808080",
              fontStyle: "italic",
            },
            "& .uneditablePackg": {
              backgroundColor: "rgba(225, 255, 255, 0)",
              color: "#808080",
            },
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            rowsPerPageOptions={[15]}
            pageSize={15}
            pagination
            loading={viewState.analysisTableLoading}
            slotProps={{
              loadingOverlay: {
                variant: 'linear-progress',
                noRowsVariant: 'skeleton',
              },
            }}
          />
        </Box>
      )}
    </div>
  );
});

function getChartData(plannedProgress, actualProgressEntry, plannedCost) {
  const data = [];
  const planned = plannedProgress.map((x) => x.expectedCost);

  const actual = actualProgressEntry.map((x) => {
    if (isNaN(parseFloat(x.actualCost, 10))) {
      return 0;
    }
    return parseFloat(x.actualCost, 10);
  });

  const earnedVal = [];

  // earnedVal[i]=((actualProgress[i]/plannedProgress[i])*plannedCost)
  // as earnedVal value is the ratio of value/cost we acheived in comparison to what was planned
  for (let i = 0; i < actualProgressEntry.length; i++) {
    let actualProgressPercent = isNaN(
      parseFloat(actualProgressEntry[i].progress)
    )
      ? 0
      : parseFloat(actualProgressEntry[i].progress);
    earnedVal.push(
      plannedProgress[i].expectedProgress !== 0
        ? (actualProgressPercent / plannedProgress[i].expectedProgress) *
        plannedProgress[i].expectedCost
        : 0
    );
  }

  data.push(planned);
  data.push(actual);
  data.push(earnedVal);

  return data;
}

const ViewChoice = ({ task, actualProgressEntry, mode}) => {
  if (task === undefined || task === null) {
    task = new Task();
  }

  if (mode === "table") {
    return (
        <TaskAnalysisTable selectedTask={task}/>
    )
  }
  else if (mode === "chart") {
    return (
      <div>
        <TaskAnalysisEChart
          task={task}
          data={getChartData(
            task.plannedProgress,
            actualProgressEntry,
            task.plannedCost
          )}
          names={["Planned Cost", "Actual Cost", "Earned Value"]}
          chartType={"bar"}
          yTitle={"Cost"}
          xTitle={"Periods"}
          title={"Task Cost"}
          percentage={false}
        />
      </div>
    );
  }
};

const TaskAnalysis = ({ task }) => {
  const [tableLoading, setTableLoading] = useState(false);
  const actualProgressEntry = task.plannedProgress.map((x) =>
    task.getActualProgressForPeriod(x.period)
  );
  const viewState = useViewState();


  
  const handleView = (event, newView) => {
    viewState.analysisTabViewMode = newView;
  };

  return (
    <div>
      <ToggleButtonGroup
        size="small"
        value={viewState.analysisTabViewMode ?? ""}
        exclusive
        onChange={handleView}
        aria-label="Analysis View"
      >
        <ToggleButton value="table" aria-label="Table View">
          <TableChartIcon />
        </ToggleButton>
        <ToggleButton value="chart" aria-label="Chart View">
          <ShowChartIcon />
        </ToggleButton>
      </ToggleButtonGroup>
      <ViewChoice
        task={task}
        actualProgressEntry={actualProgressEntry}
        mode={viewState.analysisTabViewMode}
      />
    </div>
  );
};

export default observer(TaskAnalysis);
